import { Component, computed, effect, inject, input, InputSignal, output, OutputEmitterRef, signal } from '@angular/core';
// eslint-disable-next-line max-len
import { MAT_DATE_RANGE_SELECTION_STRATEGY, MatDatepickerInputEvent, MatDateRangeInput, MatDateRangePicker, MatEndDate, MatStartDate } from '@angular/material/datepicker';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';

import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { CustomRangeSelectionStrategy } from '../../../blight-v2/shared/strategies/custom-range-selection.strategy';
import { DateService } from '../../services/date.service';
import { toLocaleSignal } from '../../utils';
import { getDefaultDiseaseWidgetSelectedDates } from '../../utils/date-range.utils';

const customRangeSelectionStrategyFactory = () =>
  new CustomRangeSelectionStrategy<Date>(3);

@Component({
  selector: 'app-disease-dashboard-widget-header',
  styleUrl: './widget.component.scss',
  template: `
    <div class="header flex--row flex--align-center">
      <div class="flex--column flex__child--grow">
        <span class="text--bold">{{ calculatedOnKey | translate }}</span>
        @if (lastCalculatedOn(); as calculatedOn) {
          <span
            data-test="last-calculated-on"
            >
            {{ calculatedOn | date: 'short' : undefined : locale() }}
          </span>
        } @else {
          <span data-test="last-calculated-on">{{ notYetCalculatedKey | translate }}</span>
        }
      </div>
      <div>
        <mat-form-field class="date-picker-input" [ngModel]="">
          <mat-date-range-input
            class="date-picker-input"
            [rangePicker]="picker"
            [min]="minimalDate()"
            [max]="maximalDate()"
            >
            <input hidden matStartDate class="date-picker-input" [ngModel]="startDatePicker()"
                   (dateChange)="onStartPickerChange($event)">
            <input hidden matEndDate class="date-picker-input" [ngModel]="endDatePicker()"
                   (dateChange)="onEndPickerChange($event)">
          </mat-date-range-input>
          <mat-date-range-picker #picker></mat-date-range-picker>
        </mat-form-field>
        <button mat-icon-button [disabled]="datePickerDisabled()" (click)="picker.open()">
          <mat-icon>calendar_today</mat-icon>
          <mat-date-range-picker #picker></mat-date-range-picker>
        </button>
        <button data-test="back-button" [disabled]="previousButtonDisabled()" mat-icon-button (click)="onPreviousDay()">
          <mat-icon>chevron_left</mat-icon>
        </button>
        <button data-test="next-button" [disabled]="nextButtonDisabled()" mat-icon-button (click)="onNextDay()">
          <mat-icon>chevron_right</mat-icon>
        </button>
      </div>
    </div>
    `,
  providers: [
    { provide: MAT_DATE_RANGE_SELECTION_STRATEGY, useFactory: customRangeSelectionStrategyFactory },
  ],
  imports: [
    MatFormField,
    FormsModule,
    MatDateRangeInput,
    MatStartDate,
    MatEndDate,
    MatDateRangePicker,
    MatIconButton,
    MatIcon,
    DatePipe,
    TranslatePipe,
  ],
})
export class DiseaseHeaderComponent {
  readonly translationPrefix: InputSignal<string> = input<string>('blightV2');
  readonly lastCalculatedOnOverride: InputSignal<Date | null> =
    input<Date | null>(null);
  readonly minimalDateOverride: InputSignal<Date | undefined | null> =
    input<Date | undefined | null>(null);
  readonly maximalDateOverride: InputSignal<Date | null> =
    input<Date | null>(null);
  readonly cropYear: InputSignal<number> = input<number>(new Date().getFullYear());
  protected readonly selectedDates = signal<Date[]>([]);
  protected readonly startDatePicker = signal<Date | null>(null);
  protected readonly endDatePicker = signal<Date | null>(null);
  protected readonly selectedDatesChanges: OutputEmitterRef<Date[]> = output<Date[]>();
  private readonly dateService = inject(DateService);
  private readonly localeService = inject(LocaleService);
  protected readonly locale = toLocaleSignal(this.localeService);
  protected readonly lastCalculatedOn = computed(() => this.lastCalculatedOnOverride());
  protected readonly minimalDate = computed(() => this.minimalDateOverride() ?? undefined);
  protected readonly previousButtonDisabled = computed(() => {
    const minDate = this.minimalDate();
    const selectedDates = this.selectedDates();
    return !minDate || selectedDates.length === 0 || this.dateService.isDateEarlierThanOrEquals(selectedDates[0], minDate);
  });
  protected readonly maximalDate = computed(() => this.maximalDateOverride() ?? undefined);
  protected readonly datePickerDisabled = computed(() => {
    const dates = this.selectedDates();
    const minAdviceDate = this.minimalDate();
    const maxAdviceDate = this.maximalDate();
    return dates.length === 0 || !minAdviceDate || !maxAdviceDate;
  });
  protected readonly nextButtonDisabled = computed(() => {
    const selectedDates = this.selectedDates();
    const maxAdviceDate = this.maximalDate();
    return !maxAdviceDate || selectedDates.length === 0
      || this.dateService.isDateLaterThanOrEquals(selectedDates.at(-1), maxAdviceDate);
  });

  public constructor() {
    let initializedCropYear: number | undefined;
    effect(() => {
      const cropYear = this.cropYear();
      if (cropYear === initializedCropYear) {
        return;
      }

      initializedCropYear = cropYear;
      this.initializeSelectedDates(cropYear);
    });
  }

  protected get calculatedOnKey(): string {
    return `${this.translationPrefix()}.shared.calculatedOn`;
  }

  protected get notYetCalculatedKey(): string {
    return `${this.translationPrefix()}.shared.notYetCalculated`;
  }

  protected onPreviousDay(): void {
    this.setSelectedDates(this.dateService.moveDateRange(this.selectedDates(), -1));
    const startDatePicker = this.startDatePicker();
    const endDatePicker = this.endDatePicker();
    if (!startDatePicker || !endDatePicker) return;
    this.startDatePicker.set(this.dateService.dayBefore(startDatePicker));
    this.endDatePicker.set(this.dateService.dayBefore(endDatePicker));
  }

  protected onNextDay(): void {
    this.setSelectedDates(this.dateService.moveDateRange(this.selectedDates(), 1));
    const startDatePicker = this.startDatePicker();
    const endDatePicker = this.endDatePicker();
    if (!startDatePicker || !endDatePicker) return;
    this.startDatePicker.set(this.dateService.dayAfter(startDatePicker));
    this.endDatePicker.set(this.dateService.dayAfter(endDatePicker));
  }

  protected onStartPickerChange(event: MatDatepickerInputEvent<Date>): void {
    this.startDatePicker.set(event.value);

    const endDatePicker = this.endDatePicker();
    if (!event.value || !endDatePicker) return;
    const datesRange = this.dateService.generateDaysBetweenDates(event.value, endDatePicker);
    this.setSelectedDates(datesRange);
  }

  protected onEndPickerChange(event: MatDatepickerInputEvent<Date>): void {
    this.endDatePicker.set(event.value);

    const startDatePicker = this.startDatePicker();
    if (!startDatePicker || !event.value) return;
    const datesRange = this.dateService.generateDaysBetweenDates(startDatePicker, event.value);
    this.setSelectedDates(datesRange);
  }

  private initializeSelectedDates(cropYear: number): void {
    const dates = getDefaultDiseaseWidgetSelectedDates(this.dateService, cropYear);

    this.startDatePicker.set(dates[0]);
    this.endDatePicker.set(dates.at(-1)!);
    this.setSelectedDates(dates);
  }

  private setSelectedDates(dates: Date[]): void {
    this.selectedDates.set(dates);
    this.selectedDatesChanges.emit(dates);
  }
}
